package com.merkost.handpass.sharedweb.state

sealed interface WebDataState<out T> {
    data object InitialLoading : WebDataState<Nothing>
    data class Content<T>(val value: T) : WebDataState<T>
    data object Empty : WebDataState<Nothing>
    data object InitialError : WebDataState<Nothing>
}

enum class WebNextPageState { Idle, Loading, Error, End }

data class WebPageRequest<K>(
    val feedKey: String,
    val generation: Int,
    val page: K,
)

data class WebPagingState<T, K>(
    val feedKey: String,
    val generation: Int,
    val phase: WebDataState<List<T>>,
    val nextPage: K,
    val nextPageState: WebNextPageState,
    val activeRequest: WebPageRequest<K>?,
)

data class WebPagingRequest<T, K>(
    val state: WebPagingState<T, K>,
    val request: WebPageRequest<K>?,
)

fun <T, K> newWebPagingState(feedKey: String, firstPage: K) = WebPagingState<T, K>(
    feedKey = feedKey,
    generation = 0,
    phase = WebDataState.InitialLoading,
    nextPage = firstPage,
    nextPageState = WebNextPageState.Idle,
    activeRequest = null,
)

fun <T, K> WebPagingState<T, K>.beginNextPage(): WebPagingRequest<T, K> {
    if (nextPageState == WebNextPageState.Loading || nextPageState == WebNextPageState.End) {
        return WebPagingRequest(this, null)
    }
    val request = WebPageRequest(feedKey, generation, nextPage)
    return WebPagingRequest(
        state = copy(nextPageState = WebNextPageState.Loading, activeRequest = request),
        request = request,
    )
}

fun <T, K> WebPagingState<T, K>.receivePage(
    request: WebPageRequest<K>,
    items: List<T>,
    nextPage: K,
    hasMore: Boolean,
    itemKey: (T) -> Any,
): WebPagingState<T, K> {
    if (request != activeRequest || request.feedKey != feedKey || request.generation != generation) return this
    val retained = (phase as? WebDataState.Content<List<T>>)?.value.orEmpty()
    val merged = (retained + items).distinctBy(itemKey)
    return copy(
        phase = if (merged.isEmpty()) WebDataState.Empty else WebDataState.Content(merged),
        nextPage = nextPage,
        nextPageState = if (hasMore) WebNextPageState.Idle else WebNextPageState.End,
        activeRequest = null,
    )
}

fun <T, K> WebPagingState<T, K>.failPage(request: WebPageRequest<K>): WebPagingState<T, K> {
    if (request != activeRequest || request.feedKey != feedKey || request.generation != generation) return this
    return copy(
        phase = if (phase is WebDataState.Content) phase else WebDataState.InitialError,
        nextPageState = WebNextPageState.Error,
        activeRequest = null,
    )
}

fun <T, K> WebPagingState<T, K>.resetFeed(feedKey: String, firstPage: K) = copy(
    feedKey = feedKey,
    generation = generation + 1,
    phase = WebDataState.InitialLoading,
    nextPage = firstPage,
    nextPageState = WebNextPageState.Idle,
    activeRequest = null,
)
